import React, { memo, useMemo } from 'react';
import { FormikProps } from 'formik';
import { SwitchProps } from '@mui/material';
import cn from 'classnames';

import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_LARGISH } from '~/Constants/UI.constants';
import { CHARCOAL_GREY, FADED_ICON_ERROR, LIGHT_BLUE } from '~/Constants/Colors.constants';
import { Typography } from '~/UIKit';
import { Switcher } from './Switcher';

const useStyles = makeStyles(
  () => ({
    container: {
      cursor: 'pointer',
    },
    icon: {
      backgroundColor: LIGHT_BLUE,
      borderRadius: 30,
      minWidth: 30,
      maxWidth: 30,
      minHeight: 30,
      maxHeight: 30,
      lineHeight: '15px',
      fontSize: FONT_SIZE_LARGISH,
      display: 'flex',
      alignItems: 'center',
      placeContent: 'center',
      marginRight: 15,
      color: CHARCOAL_GREY,
      '& > svg': {
        fontSize: 'inherit',
      },
    },
    label: {
      width: '100%',
    },
    error: {
      backgroundColor: FADED_ICON_ERROR,
    },
    disabled: {
      opacity: 0.35,
      pointerEvents: 'none',
    },
  }),
  { name: 'SwitcherWithLabel' },
);

export interface ISwitcherWithLabel extends SwitchProps {
  icon?: React.ReactNode;
  name: string;
  label?: React.ReactNode;
  value?: boolean;
  width?: number;
  errors: any;
  touched?: boolean;
  disabled: boolean;
  className?: string;
  handleBlur: FormikProps<any>['handleBlur'];
  placeholder?: string;
  handleChange: FormikProps<any>['handleChange'];
  secondaryLabel?: React.ReactNode;
  secondaryPlaceholder?: string;
}

export const SwitcherWithLabel = memo(
  ({
    icon,
    name,
    label,
    value = false,
    width,
    errors,
    touched,
    disabled,
    className,
    handleBlur: onBlur,
    placeholder,
    handleChange: onChange,
    secondaryLabel,
    secondaryPlaceholder,
  }: ISwitcherWithLabel) => {
    const { classes } = useStyles();
    const title = value ? label : secondaryLabel ?? label;
    const field = useMemo(
      () => ({
        name,
        value,
        onBlur,
        onChange,
      }),
      [name, value, onBlur, onChange],
    );

    return (
      <label
        className={cn(className, classes.container, {
          [classes.error]: touched && errors,
          [classes.disabled]: disabled,
        })}
      >
        {icon && <div className={classes.icon}>{icon}</div>}
        <Typography className={classes.label}>{title}</Typography>
        <Switcher
          name={name}
          value={value}
          width={width}
          field={field as any}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={`switcher.${name}`}
          secondaryPlaceholder={secondaryPlaceholder}
        />
      </label>
    );
  },
);
